/**
 * Usage:
 * <sc-video-play-button data-video-target="#my-video">
 *   <svg data-icon-play>...</svg>
 *   <svg data-icon-close>...</svg>
 * </sc-video-play-button>
 * 
 * Attributes:
 * - data-video-target: CSS selector for the target video container (required)
 * 
 * Child elements:
 * - [data-icon-play]: Play icon (required)
 * - [data-icon-close]: Close icon (optional - if not provided, icon won't change on play)
 */

if (!customElements.get('sc-video-play-button')) {
  class ScVideoPlayButton extends HTMLElement {
    constructor() {
      super();

      this.playIcon = this.querySelector('[data-icon-play]');
      this.closeIcon = this.querySelector('[data-icon-close]');

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      this.setAttribute('role', 'button');
      this.setAttribute('tabindex', '0');
      this.setAttribute('aria-label', 'Play video');

      if (this.closeIcon) this.closeIcon.setAttribute('hidden', '');

      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
    }

    handleClick(event) {
      event.stopPropagation();
      this.trigger();
    }

    handleKeydown(event) {
      // Handle Enter and Space keys for keyboard accessibility
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        this.trigger();
      }
    }

    trigger() {
      const videoTarget = this.dataset.videoTarget;
      if (!videoTarget) {
        console.error('[sc-video-play-button] Missing data-video-target attribute');
        return;
      }

      const videoElement = document.querySelector(videoTarget);
      if (!videoElement) {
        console.error('[sc-video-play-button] Video target not found:', videoTarget);
        return;
      }

      const isPlaying = !videoElement.hasAttribute('hidden');
      isPlaying ? this.pause(videoElement) : this.play(videoElement);
    }

    play(videoElement) {
      videoElement.removeAttribute('hidden');
      this.classList.add('is-playing');
      
      if (this.closeIcon) {
        if (this.playIcon) this.playIcon.setAttribute('hidden', '');
        this.closeIcon.removeAttribute('hidden');
        this.setAttribute('aria-label', 'Close video');
      }

      const isLoaded = videoElement.getAttribute('loaded') !== null;
      isLoaded ? this.replayVideo(videoElement) : this.loadVideo(videoElement);
    }

    pause(videoElement) {
      videoElement.setAttribute('hidden', '');

      const video = videoElement.querySelector('video');
      if (video && !video.paused) {
        video.pause();
      }

      this.classList.remove('is-playing');
      
      if (!this.closeIcon) return;
      
      if (this.playIcon) this.playIcon.removeAttribute('hidden');
      this.closeIcon.setAttribute('hidden', '');
      this.setAttribute('aria-label', "Play video");
    }

    replayVideo(videoElement) {
      const nativeVideo = videoElement.querySelector('video');
      if (!nativeVideo) return;

      nativeVideo.muted = true;
      nativeVideo.setAttribute('muted', '');
      nativeVideo.playsInline = true;
      nativeVideo.setAttribute('playsinline', '');
      nativeVideo.setAttribute('webkit-playsinline', '');
      nativeVideo.currentTime = 0;
      nativeVideo.play().catch((error) => {
        console.log('[sc-video-play-button] Video autoplay prevented:', error);
      });
    }

    loadVideo(videoElement) {
      if (typeof videoElement.loadContent === 'function') {
        videoElement.loadContent(false, false, 'video-play-button');
        // Ensure the newly injected media starts immediately after loading.
        this.replayVideo(videoElement);
        return;
      }

      const loadButton = videoElement.querySelector('.js-load-media');
      if (loadButton) {
        loadButton.click();
        this.replayVideo(videoElement);
      }
    }
  }

  customElements.define('sc-video-play-button', ScVideoPlayButton);
}
